import { Avatar, Button, Link, Stack, Typography } from '@mui/material';
import { dotDateFormatMM } from 'Config';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import { useDictionary, useIsDesktop } from 'Hooks';
import { ArrowBackBlue, SuccessRequest } from 'Images';
import { scrollToTop } from 'Utils/scrollToTop';
import { format } from 'date-fns';
import { useEffect } from 'react';

interface ISuccessDialog {
  onClickFinish: () => void;
  onClickAnotherRequest: () => void;
  requestNumber: string;
  numberOfInvoices: number;
  totalReimbursementSum: number;
}

export const SuccessDialog = ({
  onClickFinish,
  onClickAnotherRequest,
  requestNumber,
  numberOfInvoices,
  totalReimbursementSum,
}: ISuccessDialog) => {
  const dictionary = useDictionary(
    Dictionary,
    DICTIONARY.Reimbursements
  ).SuccessDialog;

  const isDesktop = useIsDesktop();

  useEffect(() => {
    scrollToTop();
  }, []);

  return (
    <Stack
      gap={isDesktop ? '24px' : '8px'}
      p={isDesktop ? '56px 110.5px' : '48px 24px 32px'}
      alignItems='center'
    >
      <Avatar
        alt=''
        src={SuccessRequest}
        sx={
          isDesktop
            ? { width: '152px', height: '103.83px' }
            : { width: '102px', height: '93px' }
        }
      />
      <Stack alignItems='center' gap='16px'>
        <Typography
          component='h1'
          variant='h3'
          fontWeight={isDesktop ? '600' : '700'}
          fontSize='24px'
          lineHeight={isDesktop ? '31px' : '28px'}
          textAlign={isDesktop ? undefined : 'center'}
        >
          {dictionary.title}
        </Typography>
        <Typography
          variant='body3'
          fontWeight={isDesktop ? '600' : '400'}
          fontSize={isDesktop ? '24px' : '18px'}
          lineHeight={isDesktop ? '31px' : '24px'}
          textAlign={isDesktop ? undefined : 'center'}
        >
          {dictionary.subtitle}
        </Typography>
      </Stack>
      <Stack
        gap='12px'
        p='20px 0px'
        width={isDesktop ? '482px' : '100%'}
        bgcolor='white'
        borderRadius='12px'
        mt={isDesktop ? undefined : '16px'}
      >
        <Stack direction='row' gap='6px' justifyContent='center'>
          <Typography variant='body3'>{dictionary.requestNumber}</Typography>
          <Typography variant='body2'>{requestNumber}</Typography>
        </Stack>
        <Stack direction='row' gap='6px' justifyContent='center'>
          <Typography variant='body3'>{dictionary.requestDate}</Typography>
          <Typography variant='body2'>
            {format(new Date(), dotDateFormatMM)}
          </Typography>
        </Stack>
        <Stack direction='row' gap='6px' justifyContent='center'>
          <Typography variant='body3'>{dictionary.numberOfInvoices}</Typography>
          <Typography variant='body2'>{numberOfInvoices}</Typography>
        </Stack>
        <Stack direction='row' gap='6px' justifyContent='center'>
          <Typography variant='body3'>
            {dictionary.totalReimbursementSum}
          </Typography>
          <Typography variant='body2'>
            {totalReimbursementSum?.toLocaleString()} ₪
          </Typography>
        </Stack>
      </Stack>
      <Stack
        direction={isDesktop ? 'row' : 'column'}
        gap='6px'
        justifyContent='center'
        alignItems='center'
        mt={isDesktop ? undefined : '16px'}
      >
        <Typography variant='body3'>{dictionary.linkPreText}</Typography>
        <Link
          sx={{
            gap: '3px',
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
          }}
          href={dictionary.linkUrl}
          underline='hover'
        >
          <Typography variant='body3'>{dictionary.linkText}</Typography>
          <Avatar
            alt=''
            src={ArrowBackBlue}
            sx={
              isDesktop
                ? { width: '20px', height: '20px' }
                : { width: '16px', height: '16px' }
            }
          />
        </Link>
      </Stack>
      <Stack
        justifyContent='center'
        gap='24px'
        alignItems='center'
        mt={isDesktop ? '40px' : '16px'}
      >
        <Button
          variant='contained'
          sx={{
            bgcolor: '#3A6EEC',
            width: isDesktop ? '234px' : '100%',
            alignItems: 'center',
            gap: '4px',
            p: isDesktop ? '12px 0px' : '8px 0px',
            height: isDesktop ? undefined : '36px',
          }}
          onClick={onClickFinish}
        >
          <Typography variant='body4'>{dictionary.finish}</Typography>
        </Button>
        <Button
          variant='outlined'
          sx={{
            bgcolor: 'white',
            width: isDesktop ? '234px' : '100%',
            alignItems: 'center',
            gap: '4px',
            p: isDesktop ? '12px 0px' : '8px 0px',
            height: isDesktop ? undefined : '36px',
          }}
          onClick={onClickAnotherRequest}
        >
          <Typography variant='body4'>{dictionary.anotherRequest}</Typography>
        </Button>
      </Stack>
    </Stack>
  );
};
